import React, { useEffect, useState } from 'react';
import {
  Streamer, StreamerAccount,
  getStreamerAccount, updateStreamerAccount, changeStreamerAccountPassword,
  deleteStreamer, createUser, deleteUser, updatePermission, adminResetPassword, syncTikTokProfile,
  setModulePermissions, addTeamLeader, removeTeamLeader,
  getTeamRemu, TeamRemu, getPeriods, getSettings, getObjectiveSettings, getStreamers,
  getStreamerThemes, getGames,
  getStreamerDebugLogs, StreamerDebugLogLine,
} from './api';
import LiveAvatar from './LiveAvatar';
import RuleEditor from './RuleEditor';
import MixingTableFullscreen from './MixingTableFullscreen';
import MesMatchs from './MesMatchs';
import MesEvenements from './MesEvenements';
import { MyLivesCard } from './Dashboard';
import NotificationsCard from './NotificationsCard';

const MEDALS: Record<string, string> = { 'Agent': '🥇', 'Co-Leader': '🥈', 'Recruteur': '🥉' };

interface Props {
  streamer: Streamer;
  onBack: () => void;
  onStreamerUpdated: () => void;
  onStreamerDeleted: () => void;
  isAdmin: boolean;
  isMobile?: boolean;
  initialTab?: Tab;
  /** Si fourni, seuls ces onglets seront visibles */
  visibleTabs?: Tab[];
}

type Tab = 'stats' | 'lives' | 'profil' | 'recompenses' | 'matchs' | 'evenements' | 'debug';

export default function StreamerDetail({ streamer, onBack, onStreamerUpdated, onStreamerDeleted, isAdmin, isMobile, initialTab, visibleTabs }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab ?? 'stats');
  const [remuEnabled, setRemuEnabled] = useState(false);
  const [showMixingTable, setShowMixingTable] = useState(false);

  useEffect(() => {
    const groupe = streamer.leader_groupe || streamer.streamer_groupe;
    if (streamer.leader_role && groupe) {
      getTeamRemu(groupe).then(r => setRemuEnabled(Boolean(r.data.enabled))).catch(() => setRemuEnabled(false));
    } else {
      setRemuEnabled(false);
    }
  }, [streamer.leader_role, streamer.leader_groupe, streamer.streamer_groupe]); // eslint-disable-line react-hooks/exhaustive-deps

  const showRecompenses = !!(streamer.leader_role && remuEnabled);

  // Fonction pour savoir si un onglet est autorisé dans ce contexte
  const isTabVisible = (t: Tab) => {
    if (visibleTabs && visibleTabs.length > 0) return visibleTabs.includes(t);
    if (t === 'recompenses') return showRecompenses;
    if (t === 'debug') return !!isAdmin;
    return true;
  };

  // Si l'onglet courant n'est pas visible, basculer sur le premier autorisé
  useEffect(() => {
    if (!isTabVisible(tab)) {
      const fallback = (['stats', 'profil', 'lives', 'matchs', 'evenements', 'recompenses'] as Tab[]).find(isTabVisible);
      if (fallback) setTab(fallback);
    }
  }, [tab, visibleTabs, showRecompenses]); // eslint-disable-line react-hooks/exhaustive-deps

  const singleTab = visibleTabs && visibleTabs.length === 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <div style={{ padding: isMobile ? '12px 16px 0' : '16px 32px 0' }}>

          {/* Breadcrumb (desktop only) */}
          {!isMobile && (
            <button onClick={onBack}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: '0 0 10px', marginBottom: 4 }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Streamers
            </button>
          )}

          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 12 : 16 }}>
            <LiveAvatar
              url={streamer.avatar_url}
              initials={streamer.display_name.slice(0, 2).toUpperCase()}
              size={isMobile ? 40 : 48}
              isLive={!!streamer.tiktok_live}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: isMobile ? 15 : 18, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{streamer.display_name}</div>
              <div style={{ color: '#94a3b8', fontSize: 12 }}>@{streamer.username}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: streamer.pc_connected ? '#16a34a' : '#d1d5db' }} />
              {!isMobile && <span style={{ color: streamer.pc_connected ? '#16a34a' : '#9ca3af', fontSize: 12 }}>{streamer.pc_connected ? 'PC connecté' : 'Hors ligne'}</span>}
            </div>
          </div>

          {/* Tabs (desktop only — masquées si un seul onglet visible) */}
          {!isMobile && !singleTab && (
            <div style={{ display: 'flex', gap: 0 }}>
              {isTabVisible('stats') && (
                <TabBtn active={tab === 'stats'} onClick={() => setTab('stats')}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Compte
                </TabBtn>
              )}
              {isTabVisible('profil') && (
                <TabBtn active={tab === 'profil'} onClick={() => setTab('profil')}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profil
                </TabBtn>
              )}
              {isTabVisible('lives') && (
                <TabBtn active={tab === 'lives'} onClick={() => setTab('lives')}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Lives interactifs
                </TabBtn>
              )}
              {isTabVisible('recompenses') && (
                <TabBtn active={tab === 'recompenses'} onClick={() => setTab('recompenses')}>
                  🏅 Récompenses
                </TabBtn>
              )}
              {isTabVisible('matchs') && (
                <TabBtn active={tab === 'matchs'} onClick={() => setTab('matchs')}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  Matchs
                </TabBtn>
              )}
              {isTabVisible('evenements') && (
                <TabBtn active={tab === 'evenements'} onClick={() => setTab('evenements')}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" strokeLinecap="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" strokeLinecap="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" strokeLinecap="round"/>
                  </svg>
                  Événements
                </TabBtn>
              )}
              {isTabVisible('debug') && (
                <TabBtn active={tab === 'debug'} onClick={() => setTab('debug')}>
                  <span style={{ marginRight: 6, fontSize: 14 }}>🐛</span>
                  Debug
                </TabBtn>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', paddingBottom: (isMobile && !singleTab) ? 58 : 0 }}>
        {tab === 'stats' && (
          <StreamerStatsTab streamer={streamer} isMobile={isMobile} />
        )}
        {tab === 'lives' && (
          <>
            {showMixingTable && (
              <MixingTableFullscreen
                streamer={streamer}
                onClose={() => setShowMixingTable(false)}
              />
            )}
            <MobileOverlayBanner streamer={streamer} isMobile={isMobile} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: isMobile ? '8px 12px 0' : '10px 24px 0' }}>
              <button
                onClick={() => setShowMixingTable(true)}
                style={{
                  background: '#0f172a', color: '#e2e8f0', border: 'none',
                  borderRadius: 8, padding: '7px 14px',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                🎛️ Table de mixage
              </button>
            </div>
            <RuleEditor streamer={streamer} onStreamerUpdated={onStreamerUpdated} isReadOnly={false} isMobile={isMobile} />
          </>
        )}
        {tab === 'profil' && (
          <AccountTab streamer={streamer} isAdmin={isAdmin} onStreamerUpdated={onStreamerUpdated} onStreamerDeleted={onStreamerDeleted} isMobile={isMobile} selfProfile={!!singleTab} />
        )}
        {tab === 'recompenses' && showRecompenses && (
          <RecompensesTab streamer={streamer} isMobile={isMobile} />
        )}
        {tab === 'matchs' && (
          <MesMatchs streamerId={streamer.id} isMobile={!!isMobile} />
        )}
        {tab === 'evenements' && (
          <MesEvenements streamerId={streamer.id} isMobile={!!isMobile} />
        )}
        {tab === 'debug' && isAdmin && (
          <DebugTab streamerId={streamer.id} isMobile={isMobile} />
        )}
      </div>

      {/* ── Mobile bottom nav — masquer si un seul onglet ── */}
      {isMobile && !singleTab && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'stretch', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <MobileBottomBtn icon={
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          } label="Retour" active={false} onClick={onBack} />
          {isTabVisible('stats') && (
            <MobileBottomBtn icon={
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            } label="Compte" active={tab === 'stats'} onClick={() => setTab('stats')} />
          )}
          {isTabVisible('profil') && (
            <MobileBottomBtn icon={
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            } label="Profil" active={tab === 'profil'} onClick={() => setTab('profil')} />
          )}
          {isTabVisible('lives') && (
            <MobileBottomBtn icon={
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            } label="Lives" active={tab === 'lives'} onClick={() => setTab('lives')} />
          )}
          {isTabVisible('recompenses') && (
            <MobileBottomBtn icon={<span style={{ fontSize: 18 }}>🏅</span>} label="Récompenses" active={tab === 'recompenses'} onClick={() => setTab('recompenses')} />
          )}
          {isTabVisible('matchs') && (
            <MobileBottomBtn icon={<span style={{ fontSize: 18 }}>🥊</span>} label="Matchs" active={tab === 'matchs'} onClick={() => setTab('matchs')} />
          )}
          {isTabVisible('evenements') && (
            <MobileBottomBtn icon={<span style={{ fontSize: 18 }}>📅</span>} label="Événements" active={tab === 'evenements'} onClick={() => setTab('evenements')} />
          )}
          {isTabVisible('debug') && (
            <MobileBottomBtn icon={<span style={{ fontSize: 18 }}>🐛</span>} label="Debug" active={tab === 'debug'} onClick={() => setTab('debug')} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Onglet Compte (stats) ─────────────────────────────────

function StreamerStatsTab({ streamer, isMobile }: { streamer: Streamer; isMobile?: boolean }) {
  const [loading, setLoading] = useState(true);
  const [sN, setSN] = useState<Streamer | null>(null);
  const [sN1, setSN1] = useState<Streamer | null>(null);
  const [periodN, setPeriodN] = useState<string | null>(null);
  const [periodN1, setPeriodN1] = useState<string | null>(null);
  const [settings, setSettings] = useState<Record<string, string | null>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [periodsRes, settingsRes] = await Promise.all([getPeriods(), getObjectiveSettings()]);
        const periods = periodsRes.data;
        if (!cancelled) setSettings(settingsRes.data);
        const pN = periods[0] ?? null;
        const pN1 = periods[1] ?? null;
        if (!cancelled) { setPeriodN(pN); setPeriodN1(pN1); }

        const [rN, rN1] = await Promise.all([
          pN ? getStreamers(pN) : getStreamers(),
          pN1 ? getStreamers(pN1) : Promise.resolve({ data: [] as Streamer[] }),
        ]);
        if (cancelled) return;
        setSN(rN.data.find(s => s.id === streamer.id) ?? null);
        setSN1(rN1.data.find(s => s.id === streamer.id) ?? null);
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [streamer.id]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>
      Chargement…
    </div>
  );

  if (!sN) return (
    <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
      Aucune donnée disponible pour ce streamer.
    </div>
  );

  const objDiamonds = parseInt(settings['objective_diamonds'] ?? '750', 10) || 750;
  const objDays = parseInt(settings['objective_days'] ?? '8', 10) || 8;
  const objHours = parseInt(settings['objective_hours'] ?? '20', 10) || 20;

  const fmtNum = (n: number | null | undefined) => n != null ? n.toLocaleString('fr-FR') : '—';
  const fmtHours = (mins: number | null | undefined) => {
    if (mins == null) return '—';
    const h = Math.floor(mins / 60); const m = Math.round(mins % 60);
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  };
  const calcPct = (n: number | null | undefined, n1: number | null | undefined) => {
    if (n == null || n1 == null || n1 === 0) return null;
    return Math.round(((n - n1) / n1) * 100);
  };
  const fmtPeriod = (p: string) => {
    const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
    const parts = p.split('-');
    if (parts.length < 2) return p;
    return `${months[parseInt(parts[1], 10) - 1] ?? p} ${parts[0]}`;
  };

  const EvoBadge = ({ pct }: { pct: number | null }) => {
    if (pct === null) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
    const color = pct > 0 ? '#16a34a' : pct < 0 ? '#dc2626' : '#64748b';
    const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '=';
    return <span style={{ color, fontSize: 11, fontWeight: 600 }}>{arrow} {Math.abs(pct)}%</span>;
  };

  const Gauge = ({ value, target, label }: { value: number | null; target: number; label: string }) => {
    const v = value ?? 0;
    const pct = Math.min(Math.round((v / target) * 100), 100);
    const barColor = pct >= 100 ? '#16a34a' : pct >= 70 ? '#2563eb' : '#f59e0b';
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
          <span>{label}</span>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{fmtNum(v)} / {fmtNum(target)} <span style={{ color: barColor }}>({pct}%)</span></span>
        </div>
        <div style={{ height: 7, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.4s' }} />
        </div>
      </div>
    );
  };

  const Metric = ({ icon, label, value, pct }: { icon: string; label: string; value: string; pct: number | null }) => (
    <div style={{ flex: '1 1 0', minWidth: 0, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 18 }}>{icon}</div>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{value}</div>
      <EvoBadge pct={pct} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 12px' : '20px 28px' }}>
      {/* Period */}
      {(periodN || periodN1) && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{periodN ? fmtPeriod(periodN) : '—'}</span>
          {periodN1 && <><span style={{ color: '#cbd5e1' }}>vs</span><span>{fmtPeriod(periodN1)}</span></>}
        </div>
      )}

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Metric icon="💎" label="Diamants" value={fmtNum(sN.diamonds)} pct={calcPct(sN.diamonds, sN1?.diamonds)} />
        <Metric icon="📅" label="Jours live" value={fmtNum(sN.live_days)} pct={calcPct(sN.live_days, sN1?.live_days)} />
        <Metric icon="⏱" label="Heures live" value={fmtHours(sN.live_duration_minutes)} pct={calcPct(sN.live_duration_minutes, sN1?.live_duration_minutes)} />
        <Metric icon="👥" label="Nouveaux abonnés" value={sN.new_followers != null ? fmtNum(sN.new_followers) : '—'} pct={calcPct(sN.new_followers, sN1?.new_followers)} />
      </div>

      {/* Objectives + TikTok status */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 14 }}>Objectifs du mois</div>
        <Gauge value={sN.diamonds ?? null} target={objDiamonds} label="💎 Diamants" />
        <Gauge value={sN.live_days ?? null} target={objDays} label="📅 Jours live" />
        <Gauge value={sN.live_duration_minutes != null ? Math.round(sN.live_duration_minutes / 60) : null} target={objHours} label="⏱ Heures live" />

        {/* TikTok status — mirrors Dashboard StreamerTab */}
        {(() => {
          // Helpers (same logic as Dashboard)
          type EchelonSt = 'montee' | 'maintien' | 'non_maintenu' | null;
          const parseEchelon = (e: string | null | undefined): EchelonSt => {
            if (!e || e === '-') return null;
            const s = e.toLowerCase();
            if (s.includes('non maintenu')) return 'non_maintenu';
            if (s.includes('montée') || s.includes('supérieur') || s.includes('augmentation')) return 'montee';
            if (s.includes('maintien') || s.includes('maintenu')) return 'maintien';
            return null;
          };
          const THRESHOLDS = [
            { tier: 5, days: 22, hours: 80 }, { tier: 4, days: 18, hours: 60 },
            { tier: 3, days: 15, hours: 40 }, { tier: 2, days: 11, hours: 30 },
            { tier: 1, days: 7,  hours: 20 },
          ] as const;
          const days = sN.live_days ?? 0;
          const mins = sN.live_duration_minutes ?? 0;
          const activiteTier = THRESHOLDS.find(t => days >= t.days && mins >= t.hours * 60)?.tier ?? 0;
          const nextTier = THRESHOLDS.slice().reverse().find(t => t.tier === activiteTier + 1) ?? null;
          const echelonSt = parseEchelon(sN.statut_echelon);
          const niveauCondDays = days >= 7;
          const niveauCondHours = mins >= 900;

          const echelonBadge = () => {
            if (!echelonSt) return <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>;
            const cfg = {
              montee:       { label: '▲ Montée',        bg: '#dbeafe', color: '#1d4ed8' },
              maintien:     { label: '✓ Maintenu',       bg: '#dcfce7', color: '#16a34a' },
              non_maintenu: { label: '⚠ Non maintenu',  bg: '#fef3c7', color: '#d97706' },
            } as const;
            const c = cfg[echelonSt];
            return <span style={{ fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, borderRadius: 10, padding: '2px 9px' }}>{c.label}</span>;
          };

          const tierLabel = activiteTier === 0
            ? <span style={{ fontSize: 12, color: '#94a3b8' }}>Inactif</span>
            : <span style={{ fontSize: 12, fontWeight: 600, background: '#f0fdf4', color: '#16a34a', borderRadius: 10, padding: '2px 9px' }}>Niveau {activiteTier}/5</span>;

          return (
            <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 14, paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Statut TikTok</div>

              {/* Montée de niveau */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>📊 Montée de niveau</span>
                {echelonBadge()}
              </div>
              {sN.statut_evolution && sN.statut_evolution !== '-' && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, paddingLeft: 2 }}>{sN.statut_evolution}</div>
              )}
              <div style={{ fontSize: 11, marginBottom: 12, display: 'flex', gap: 10 }}>
                <span style={{ color: niveauCondDays ? '#16a34a' : '#f59e0b' }}>{niveauCondDays ? '✓' : '✗'} {days}/7 jours</span>
                <span style={{ color: niveauCondHours ? '#16a34a' : '#f59e0b' }}>{niveauCondHours ? '✓' : '✗'} {fmtHours(mins)}/15h</span>
              </div>

              {/* Niveau d'activité */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>⚡ Niveau d'activité</span>
                {tierLabel}
              </div>
              {activiteTier < 5 && nextTier && (
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Prochain niv. {nextTier.tier} : {nextTier.days}j et {nextTier.hours}h
                  {' · '}<span style={{ color: days >= nextTier.days ? '#16a34a' : '#f59e0b' }}>{days}/{nextTier.days}j</span>
                  {' · '}<span style={{ color: mins >= nextTier.hours * 60 ? '#16a34a' : '#f59e0b' }}>{fmtHours(mins)}/{nextTier.hours}h</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Card « Mes lives » (sessions live du mois en cours) */}
        <MyLivesCard streamerId={streamer.id} isMobile={isMobile} />
      </div>
    </div>
  );
}

// ── Onglet Profil ─────────────────────────────────────────

function AccountTab({ streamer, isAdmin, onStreamerUpdated, onStreamerDeleted, isMobile, selfProfile }: {
  streamer: Streamer; isAdmin: boolean;
  onStreamerUpdated: () => void; onStreamerDeleted: () => void;
  isMobile?: boolean; selfProfile?: boolean;
}) {
  const [account, setAccount] = useState<StreamerAccount | null>(null);
  const [form, setForm] = useState({ display_name: '', tiktok_username: '', first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const handleSyncTikTok = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await syncTikTokProfile(streamer.id);
      const name = r.data.display_name ?? '';
      setSyncMsg({ ok: `Synchronisé${name ? ` — ${name}` : ''}` });
      onStreamerUpdated();
      await load();
      setTimeout(() => setSyncMsg(null), 4000);
    } catch (e: any) {
      setSyncMsg({ err: e?.response?.data?.detail || 'Erreur de synchronisation' });
    }
    setSyncing(false);
  };

  useEffect(() => { load(); }, [streamer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const r = await getStreamerAccount(streamer.id);
      setAccount(r.data);
      setForm({
        display_name: r.data.display_name,
        tiktok_username: r.data.account?.tiktok_username ?? '',
        first_name: r.data.account?.first_name ?? '',
        last_name: r.data.account?.last_name ?? '',
        email: r.data.account?.email ?? '',
        phone: r.data.account?.phone ?? '',
      });
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true); setSavedMsg('');
    try {
      const r = await updateStreamerAccount(streamer.id, {
        display_name: form.display_name || undefined,
        tiktok_username: form.tiktok_username || null,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        phone: form.phone || null,
      });
      setAccount(r.data); setSavedMsg('Sauvegardé');
      onStreamerUpdated();
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { setSavedMsg('Erreur'); }
    setSaving(false);
  };


  const handlePasswordChange = async () => {
    if (!pwForm.next) { setPwMsg({ err: 'Nouveau mot de passe requis' }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ err: 'Les mots de passe ne correspondent pas' }); return; }
    if (pwForm.next.length < 6) { setPwMsg({ err: 'Minimum 6 caractères' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await changeStreamerAccountPassword(streamer.id, pwForm.current, pwForm.next);
      setPwMsg({ ok: 'Mot de passe changé' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err: any) {
      setPwMsg({ err: err?.response?.data?.detail || 'Erreur' });
    }
    setPwSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${streamer.display_name}" ? Cette action est irréversible.`)) return;
    await deleteStreamer(streamer.id);
    onStreamerDeleted();
  };

  const acc = account?.account;
  const avatarSrc = streamer.avatar_url ?? null;
  const initials = ((acc?.first_name?.[0] ?? '') + (acc?.last_name?.[0] ?? '')).toUpperCase()
    || (account?.display_name?.slice(0, 2) ?? '').toUpperCase();

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 16px' : '28px 32px' }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 20, alignItems: 'flex-start' }}>

        {/* ── Left column ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Photo de profil */}
          <Card title="Photo de profil">
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #ddd6fe', flexShrink: 0 }}>
                {avatarSrc ? (
                  <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                ) : (
                  <span style={{ color: '#7c3aed', fontSize: 24, fontWeight: 700 }}>{initials || '?'}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {isAdmin && (
                  <button onClick={handleSyncTikTok} disabled={syncing}
                    style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>🔄</span>
                    {syncing ? 'Synchronisation…' : 'Sync TikTok'}
                  </button>
                )}
                <div style={{ color: '#94a3b8', fontSize: 11 }}>Photo synchronisée depuis TikTok</div>
                {syncMsg?.ok && <div style={{ color: '#16a34a', fontSize: 12 }}>✓ {syncMsg.ok}</div>}
                {syncMsg?.err && <div style={{ color: '#dc2626', fontSize: 12 }}>✗ {syncMsg.err}</div>}
              </div>
            </div>
          </Card>

          {/* Informations */}
          <Card title="Informations">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <LightField label="Nom affiché">
                <input style={inputLight} value={form.display_name} placeholder="Haribo Live"
                  onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} />
              </LightField>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <LightField label="Prénom">
                  <input style={inputLight} value={form.first_name} placeholder="Jean"
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                </LightField>
                <LightField label="Nom">
                  <input style={inputLight} value={form.last_name} placeholder="Dupont"
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </LightField>
              </div>
              <LightField label="Identifiant TikTok">
                <div style={{ display: 'flex' }}>
                  <span style={{ padding: '9px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRight: 'none', borderRadius: '8px 0 0 8px', color: '#94a3b8', fontSize: 13 }}>@</span>
                  <input style={{ ...inputLight, borderRadius: '0 8px 8px 0', flex: 1 }} value={form.tiktok_username} placeholder="pseudotiktok"
                    onChange={e => setForm(f => ({ ...f, tiktok_username: e.target.value }))} />
                </div>
              </LightField>
              <LightField label="Adresse e-mail">
                <input style={inputLight} type="email" value={form.email} placeholder="jean@example.com"
                  autoComplete="off"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </LightField>
              <LightField label="📱 Téléphone mobile">
                <input style={inputLight} type="tel" value={form.phone} placeholder="+33 6 12 34 56 78"
                  autoComplete="off"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Pour recevoir les <strong>rappels de match par SMS</strong> avant chaque live.
                </div>
              </LightField>
            </div>
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
              {savedMsg && <span style={{ color: savedMsg === 'Sauvegardé' ? '#16a34a' : '#dc2626', fontSize: 13 }}>{savedMsg}</span>}
            </div>
          </Card>

          {/* Mot de passe */}
          {acc && (
            <Card title="Changer le mot de passe">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {!isAdmin && (
                  <LightField label="Mot de passe actuel">
                    <input style={inputLight} type="password" value={pwForm.current} placeholder="••••••••"
                      onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
                  </LightField>
                )}
                <LightField label="Nouveau mot de passe">
                  <input style={inputLight} type="password" value={pwForm.next} placeholder="••••••••"
                    onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
                </LightField>
                <LightField label="Confirmer">
                  <input style={inputLight} type="password" value={pwForm.confirm} placeholder="••••••••"
                    onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
                </LightField>
              </div>
              {pwMsg && (
                <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: pwMsg.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${pwMsg.ok ? '#bbf7d0' : '#fecaca'}`, color: pwMsg.ok ? '#16a34a' : '#dc2626', fontSize: 13 }}>
                  {pwMsg.ok || pwMsg.err}
                </div>
              )}
              <button onClick={handlePasswordChange} disabled={pwSaving} style={{ ...btnSecondary, marginTop: 12 }}>
                {pwSaving ? 'Changement…' : 'Changer le mot de passe'}
              </button>
            </Card>
          )}

          {/* Notifications — uniquement sur SA propre fiche (vue « Mon profil ») */}
          {selfProfile && <NotificationsCard />}

          {/* Zone admin — delete */}
          {isAdmin && (
            <Card title="Administration">
              <button onClick={handleDelete}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                Supprimer ce streamer
              </button>
            </Card>
          )}
        </div>

        {/* ── Right column — admin only ── */}
        {isAdmin && (
          <div style={isMobile ? { width: '100%', minWidth: 0 } : { width: 300, flexShrink: 0 }}>
            <AccountUserCard streamerId={streamer.id} account={acc ?? null} onRefresh={load} streamerObj={streamer} onStreamerUpdated={onStreamerUpdated} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Carte compte utilisateur (admin) ─────────────────────

function AccountUserCard({ streamerId, account, onRefresh, streamerObj, onStreamerUpdated }: {
  streamerId: number;
  account: StreamerAccount['account'];
  onRefresh: () => void;
  streamerObj?: Streamer;
  onStreamerUpdated?: () => void;
}) {
  const [createForm, setCreateForm] = useState({ username: '', password: '', permission: 'read' });
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const [resetPwd, setResetPwd] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const [togglingPerm, setTogglingPerm] = useState(false);

  // Droits modules (responsable)
  const [modPerms, setModPerms] = useState({
    can_manage_matchs: !!account?.can_manage_matchs,
    can_manage_events: !!account?.can_manage_events,
    can_manage_lives: !!account?.can_manage_lives,
    can_manage_themes: !!account?.can_manage_themes,
  });
  const [modSaving, setModSaving] = useState(false);
  React.useEffect(() => {
    setModPerms({
      can_manage_matchs: !!account?.can_manage_matchs,
      can_manage_events: !!account?.can_manage_events,
      can_manage_lives: !!account?.can_manage_lives,
      can_manage_themes: !!account?.can_manage_themes,
    });
  }, [account?.can_manage_matchs, account?.can_manage_events, account?.can_manage_lives, account?.can_manage_themes]);
  const toggleMod = async (key: 'can_manage_matchs' | 'can_manage_events' | 'can_manage_lives' | 'can_manage_themes') => {
    if (!account) return;
    const next = { ...modPerms, [key]: !modPerms[key] };
    setModPerms(next); setModSaving(true);
    try { await setModulePermissions(account.id, next); onRefresh(); } catch {}
    setModSaving(false);
  };

  // Leader status
  const [leaderStatus, setLeaderStatus] = useState(streamerObj?.leader_role || '');
  const [leaderSaving, setLeaderSaving] = useState(false);
  React.useEffect(() => { setLeaderStatus(streamerObj?.leader_role || ''); }, [streamerObj?.leader_role]);

  const handleLeaderChange = async (newRole: string) => {
    setLeaderSaving(true);
    try {
      const groupe = streamerObj?.streamer_groupe || streamerObj?.leader_groupe;
      if (!newRole) {
        if (streamerObj?.leader_groupe) {
          await removeTeamLeader(streamerObj.leader_groupe, streamerId);
        }
      } else if (groupe) {
        await addTeamLeader(groupe, streamerId, newRole);
      }
      setLeaderStatus(newRole);
      onStreamerUpdated?.();
    } catch {}
    setLeaderSaving(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.username.trim() || !createForm.password.trim()) return;
    setCreating(true); setCreateMsg(null);
    try {
      await createUser({
        username: createForm.username.trim(),
        password: createForm.password.trim(),
        role: 'streamer',
        streamer_id: streamerId,
        permission: createForm.permission,
      });
      setCreateForm({ username: '', password: '', permission: 'read' });
      setCreateMsg({ ok: 'Compte créé' });
      onRefresh();
    } catch (err: any) {
      setCreateMsg({ err: err?.response?.data?.detail || 'Erreur lors de la création' });
    }
    setCreating(false);
  };

  const handleDelete = async () => {
    if (!account || !window.confirm(`Supprimer le compte "${account.username}" ?`)) return;
    await deleteUser(account.id);
    onRefresh();
  };

  const handleTogglePerm = async () => {
    if (!account) return;
    setTogglingPerm(true);
    const next = account.permission === 'write' ? 'read' : 'write';
    await updatePermission(account.id, next);
    onRefresh();
    setTogglingPerm(false);
  };

  const handleResetPwd = async () => {
    if (!account || !resetPwd?.trim()) return;
    setResetting(true); setResetMsg(null);
    try {
      await adminResetPassword(account.id, resetPwd.trim());
      setResetMsg({ ok: 'Mot de passe modifié' });
      setResetPwd(null);
    } catch { setResetMsg({ err: 'Erreur' }); }
    setResetting(false);
  };

  return (
    <Card title="Compte utilisateur">
      {!account ? (
        /* ── Create form ── */
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 2 }}>
            Aucun compte lié à ce streamer.
          </div>
          <LightField label="Identifiant">
            <input style={inputLight} value={createForm.username} placeholder="pseudo"
              onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} />
          </LightField>
          <LightField label="Mot de passe">
            <input style={inputLight} type="password" value={createForm.password} placeholder="••••••"
              onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} />
          </LightField>
          <LightField label="Permission">
            <select style={{ ...inputLight, cursor: 'pointer' }} value={createForm.permission}
              onChange={e => setCreateForm(f => ({ ...f, permission: e.target.value }))}>
              <option value="read">Lecture seule</option>
              <option value="write">Lecture + Écriture</option>
            </select>
          </LightField>
          {createMsg && (
            <div style={{ padding: '7px 10px', borderRadius: 7, background: createMsg.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${createMsg.ok ? '#bbf7d0' : '#fecaca'}`, color: createMsg.ok ? '#16a34a' : '#dc2626', fontSize: 12 }}>
              {createMsg.ok || createMsg.err}
            </div>
          )}
          <button type="submit" disabled={creating || !createForm.username.trim() || !createForm.password.trim()}
            style={{ ...btnPrimary, opacity: (!createForm.username.trim() || !createForm.password.trim()) ? 0.5 : 1, marginTop: 2 }}>
            {creating ? 'Création…' : 'Créer le compte'}
          </button>
        </form>
      ) : (
        /* ── Existing account ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Username row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#2563eb' }}>
                {account.username.charAt(0).toUpperCase()}
              </div>
              {leaderStatus && MEDALS[leaderStatus] && (
                <span style={{ position: 'absolute', bottom: -3, right: -3, fontSize: 12, lineHeight: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))', pointerEvents: 'none' }}>
                  {MEDALS[leaderStatus]}
                </span>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.username}</div>
              <div style={{ color: '#94a3b8', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                {leaderStatus ? <>{MEDALS[leaderStatus]} {leaderStatus}</> : 'Streamer'}
                {(streamerObj?.streamer_groupe || streamerObj?.leader_groupe) && (
                  <span style={{ opacity: 0.6 }}>· {streamerObj.streamer_groupe || streamerObj.leader_groupe}</span>
                )}
              </div>
            </div>
          </div>

          {/* Statut responsable */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
            <span style={{ color: '#374151', fontSize: 12 }}>Statut</span>
            <select value={leaderStatus} disabled={leaderSaving || !(streamerObj?.streamer_groupe || streamerObj?.leader_groupe)}
              onChange={e => handleLeaderChange(e.target.value)}
              title={!(streamerObj?.streamer_groupe || streamerObj?.leader_groupe) ? 'Aucune équipe associée à ce streamer' : undefined}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#fff', outline: 'none', opacity: leaderSaving ? 0.6 : 1 }}>
              <option value="">Streamer</option>
              <option value="Agent">🥇 Agent</option>
              <option value="Co-Leader">🥈 Co-Leader</option>
              <option value="Recruteur">🥉 Recruteur</option>
            </select>
          </div>

          {/* Permission */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
            <span style={{ color: '#374151', fontSize: 12 }}>Permission</span>
            <button onClick={handleTogglePerm} disabled={togglingPerm}
              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: account.permission === 'write' ? '#dcfce7' : '#f1f5f9', color: account.permission === 'write' ? '#16a34a' : '#64748b', transition: 'all 0.15s' }}>
              {account.permission === 'write' ? '✓ Écriture' : 'Lecture seule'}
            </button>
          </div>

          {/* Droits modules (responsable uniquement) */}
          {leaderStatus && (
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
              <div style={{ color: '#374151', fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Droits responsable</div>
              <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8 }}>
                Accès + création pour les streamers de son équipe
              </div>
              {([
                ['can_manage_matchs', '🥊 Lives Matchs'],
                ['can_manage_events', '📅 Événements Live'],
                ['can_manage_lives', '🎮 Lives Interactifs'],
                ['can_manage_themes', '🎨 Créer des thèmes'],
              ] as const).map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, cursor: modSaving ? 'wait' : 'pointer', fontSize: 12, color: '#374151' }}>
                  <input type="checkbox" checked={modPerms[key]} disabled={modSaving}
                    onChange={() => toggleMod(key)}
                    style={{ width: 16, height: 16, accentColor: '#7c3aed', cursor: 'inherit' }} />
                  {label}
                </label>
              ))}
              {modPerms.can_manage_themes && (
                <div style={{ fontSize: 10.5, color: '#94a3b8', padding: '0 10px 4px' }}>
                  🎨 permet de créer/modifier les thèmes du live interactif (bibliothèque partagée).
                </div>
              )}
            </div>
          )}

          {/* Reset password */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <div style={{ color: '#374151', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Réinitialiser le mot de passe</div>
            {resetPwd !== null ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="password" placeholder="Nouveau mot de passe" value={resetPwd} autoFocus
                  onChange={e => setResetPwd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleResetPwd()}
                  style={{ ...inputLight, flex: 1, fontSize: 12, padding: '7px 10px' }} />
                <button onClick={handleResetPwd} disabled={resetting || !resetPwd.trim()}
                  style={{ padding: '7px 10px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', opacity: !resetPwd.trim() ? 0.5 : 1 }}>
                  {resetting ? '…' : 'OK'}
                </button>
                <button onClick={() => { setResetPwd(null); setResetMsg(null); }}
                  style={{ padding: '7px 8px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            ) : (
              <button onClick={() => setResetPwd('')} style={{ ...btnSecondary, fontSize: 12, padding: '7px 12px' }}>
                Changer le mot de passe
              </button>
            )}
            {resetMsg && (
              <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 7, background: resetMsg.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${resetMsg.ok ? '#bbf7d0' : '#fecaca'}`, color: resetMsg.ok ? '#16a34a' : '#dc2626', fontSize: 12 }}>
                {resetMsg.ok || resetMsg.err}
              </div>
            )}
          </div>

          {/* Delete account */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <button onClick={handleDelete}
              style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
              Supprimer le compte
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Onglet Debug (admin) ──────────────────────────────────
// Polle les logs backend filtrés sur le streamer (équivalent de
// `tail -f /opt/tiktok-animator/logs/backend.log | grep <username>`).

function DebugTab({ streamerId, isMobile }: { streamerId: number; isMobile?: boolean }) {
  const [lines, setLines] = useState<StreamerDebugLogLine[]>([]);
  const [username, setUsername] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const load = React.useCallback(async () => {
    try {
      const r = await getStreamerDebugLogs(streamerId, 300);
      setLines(r.data.lines || []);
      setUsername(r.data.username || '');
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur de chargement des logs');
    }
  }, [streamerId]);

  useEffect(() => {
    if (paused) return;
    load();
    const iv = setInterval(load, 2500);
    return () => clearInterval(iv);
  }, [load, paused]);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  const colorize = (s: string): string => {
    if (s.includes('Cadeau reçu')) return '#7c3aed';
    if (s.includes('→ LIVE')) return '#16a34a';
    if (s.includes('→ offline')) return '#94a3b8';
    if (s.includes('Connecté au live')) return '#2563eb';
    if (s.includes('Déconnecté du live')) return '#f59e0b';
    if (s.includes('Listener démarré')) return '#0ea5e9';
    if (/Erreur|Error|error/i.test(s)) return '#dc2626';
    return '#e2e8f0';
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(lines.map(l => `[${l.source}] ${l.text}`).join('\n'));
    } catch {}
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: isMobile ? '10px 12px' : '16px 24px', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>🐛 Debug logs en direct</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          {username ? <>filtre : <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{username}</code></> : 'aucun streamer'}
        </span>
        <span style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
          <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} />
          Auto-scroll
        </label>
        <button onClick={() => setPaused(p => !p)}
          style={{ background: paused ? '#fef3c7' : '#f0fdf4', color: paused ? '#92400e' : '#15803d', border: `1px solid ${paused ? '#fcd34d' : '#bbf7d0'}`, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {paused ? '▶ Reprendre' : '⏸ Pause'}
        </button>
        <button onClick={load}
          style={{ background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          ⟳ Rafraîchir
        </button>
        <button onClick={copyAll}
          style={{ background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          📋 Copier
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
          {error}
        </div>
      )}

      <div ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto', minHeight: 0,
          background: '#0b1020', color: '#e2e8f0',
          borderRadius: 10, padding: 12,
          fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
          fontSize: 12, lineHeight: 1.5,
        }}
      >
        {lines.length === 0 ? (
          <div style={{ color: '#94a3b8' }}>
            Aucune ligne pour <code>{username || '—'}</code>. Le streamer doit être en live ou avoir une activité récente.
            <br /><br />
            La page polle les logs backend toutes les 2,5 s.
          </div>
        ) : (
          lines.map((l, i) => (
            <div key={i} style={{ color: colorize(l.text), whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <span style={{ color: '#475569', marginRight: 6 }}>[{l.source}]</span>
              {l.text}
            </div>
          ))
        )}
      </div>

      <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>
        {lines.length} ligne{lines.length > 1 ? 's' : ''} · mise à jour auto toutes les 2,5 s
      </div>
    </div>
  );
}

// ── Bannière mode overlay mobile ──────────────────────────

function MobileBottomBtn({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ flex: 1, padding: '8px 4px 6px', border: 'none', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: active ? '#2563eb' : '#64748b', cursor: 'pointer', borderTop: `2px solid ${active ? '#2563eb' : 'transparent'}` }}>
      <span style={{ display: 'flex' }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{label}</span>
    </button>
  );
}

export function MobileOverlayBanner({ streamer, isMobile }: { streamer: Streamer; isMobile?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [activeThemeName, setActiveThemeName] = useState<string | null>(null);
  const [activeGameName, setActiveGameName] = useState<string | null>(null);
  const streamerId = streamer.id;
  const mobileUrl = `${window.location.protocol}//${window.location.host}/overlay/${streamerId}`;
  const pcUrl = `${window.location.protocol}//${window.location.host}/nsstream/${streamerId}`;

  useEffect(() => {
    if (isMobile) return;
    let cancelled = false;
    Promise.all([
      getStreamerThemes(streamerId),
      getGames(true),
    ]).then(([themesRes, gamesRes]) => {
      if (cancelled) return;
      const activeTheme = themesRes.data.find(t => t.id === streamer.active_theme_id || t.is_active === 1);
      const activeGame = gamesRes.data.find(g => g.id === streamer.active_game_id);
      setActiveThemeName(activeTheme?.name ?? null);
      setActiveGameName(activeGame?.name ?? null);
    }).catch(() => {
      if (cancelled) return;
      setActiveThemeName(null);
      setActiveGameName(null);
    });
    return () => { cancelled = true; };
  }, [isMobile, streamerId, streamer.active_theme_id, streamer.active_game_id]);

  useEffect(() => {
    if (isMobile) return;
    const onActiveContentChange = (event: Event) => {
      const detail = (event as CustomEvent<{
        streamerId?: number;
        activeThemeName?: string | null;
        activeGameName?: string | null;
      }>).detail;
      if (!detail || detail.streamerId !== streamerId) return;
      if ('activeThemeName' in detail) setActiveThemeName(detail.activeThemeName ?? null);
      if ('activeGameName' in detail) setActiveGameName(detail.activeGameName ?? null);
    };
    window.addEventListener('nslive-active-content-change', onActiveContentChange);
    return () => window.removeEventListener('nslive-active-content-change', onActiveContentChange);
  }, [isMobile, streamerId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(pcUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (isMobile) {
    return (
      <div style={{ flexShrink: 0, margin: '12px 14px 0', display: 'flex' }}>
        <a href={mobileUrl} target="_blank" rel="noopener noreferrer" style={{
          flex: 1, padding: '12px 16px', borderRadius: 12,
          background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
          color: '#fff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10,
          fontWeight: 600, fontSize: 14,
        }}>
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Ouvrir l'overlay mobile
        </a>
      </div>
    );
  }

  return (
    <div style={{
      flexShrink: 0, margin: '12px 24px 0',
      background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
      border: '1px solid #bfdbfe',
      borderRadius: 12, padding: '12px 16px',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#2563eb" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#1d4ed8', fontWeight: 600, fontSize: 13, marginBottom: 2 }}>TikTok Live Studio — Source navigateur</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 4px' }}>
          <span style={{ color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Thème : {activeThemeName ?? 'Aucun'}
          </span>
          <span style={{ color: '#047857', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Jeu : {activeGameName ?? 'Aucun'}
          </span>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pcUrl}</div>
      </div>
      <button onClick={handleCopy} style={{
        padding: '7px 14px', borderRadius: 8, border: '1px solid #bfdbfe',
        background: copied ? '#2563eb' : '#fff', color: copied ? '#fff' : '#2563eb',
        fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
        transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}>
        {copied ? '✓ Copié !' : 'Copier le lien'}
      </button>
    </div>
  );

}

// ── Onglet Récompenses ────────────────────────────────────

function RecompensesTab({ streamer, isMobile }: {
  streamer: Streamer;
  isMobile?: boolean;
}) {
  const groupe = streamer.leader_groupe || streamer.streamer_groupe;
  const [remu, setRemu] = useState<TeamRemu | null>(null);
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(undefined);
  const [objectives, setObjectives] = useState({ days: 8, hours: 20, diamonds: 750 });

  useEffect(() => {
    getPeriods().then(r => {
      // Dédupliquer : un seul import par mois calendaire (le plus récent)
      const seen = new Set<string>();
      const deduped = r.data.filter(p => {
        const key = p.match(/^(\d{4}-\d{2})/)?.[1] ?? p;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setPeriods(deduped);
    }).catch(() => {});
    getSettings().then(r => {
      const d = r.data;
      setObjectives({
        days:     parseInt(d['objective_days']     || '8')   || 8,
        hours:    parseInt(d['objective_hours']    || '20')  || 20,
        diamonds: parseInt(d['objective_diamonds'] || '750') || 750,
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!groupe) return;
    setLoading(true);
    getTeamRemu(groupe, selectedPeriod).then(r => { setRemu(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [groupe, selectedPeriod]); // eslint-disable-line

  if (!groupe) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8', fontSize:14 }}>
      Ce streamer n'est pas associé à une équipe.
    </div>
  );

  const members = remu?.members || [];
  const fmtR = (v?: number|null) => (v != null && v > 0 ? `$${v.toFixed(2)}` : '—');
  const fmtN = (n?: number|null) => n != null ? n.toLocaleString('fr-FR') : '—';
  const fmtDur = (mins?: number|null) => (!mins ? '—' : `${Math.floor(mins/60)}h${(mins%60)>0?` ${mins%60}min`:''}`);

  // Couleur jauge objectifs
  const objColor = (pct: number) => pct >= 1 ? '#16a34a' : pct >= 0.5 ? '#ea580c' : '#dc2626';
  const totalDiamonds = members.reduce((s, m) => s + (m.diamonds || 0), 0);

  return (
    <div style={{ flex:1, overflow:'auto', padding: isMobile ? '14px' : '24px 32px', display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── En-tête : équipe + sélecteur période ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:0.8 }}>Équipe</div>
          <div style={{ fontSize:15, fontWeight:700, color:'#0f172a' }}>🏆 {groupe}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:12, color:'#64748b' }}>Période :</span>
          <select value={selectedPeriod||''} onChange={e => setSelectedPeriod(e.target.value||undefined)}
            style={{ fontSize:12, padding:'5px 8px', borderRadius:7, border:'1px solid #e2e8f0', color:'#374151', background:'#fff' }}>
            <option value="">Dernière période</option>
            {periods.map(p => <option key={p} value={p}>{fmtPeriodLabel(p)}</option>)}
          </select>
        </div>
      </div>

      {/* ── Indicateurs ── */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr', gap:10 }}>
        {[
          { label:'💎 Total diamants', value: fmtN(totalDiamonds), color:'#0f172a', fontSize: isMobile ? 14 : 17 },
          { label:'📈 Montée niv.',    value: fmtR(remu?.total_team_niveau),   color:'#6366f1', fontSize: isMobile ? 14 : 17 },
          { label:'⚡ Activité',       value: fmtR(remu?.total_team_activite), color:'#f59e0b', fontSize: isMobile ? 14 : 17 },
          { label:'🏅 Total récomp.',  value: fmtR(remu?.total_team_remu),     color:'#16a34a', fontSize: isMobile ? 15 : 20 },
        ].map(({ label, value, color, fontSize }) => (
          <div key={label} style={{ background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding: isMobile ? '10px 12px' : '14px 18px', textAlign:'center' }}>
            <div style={{ fontSize: isMobile ? 9 : 11, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:0.6, marginBottom:4 }}>{label}</div>
            <div style={{ fontSize, fontWeight:700, color }}>{loading ? '…' : value}</div>
          </div>
        ))}
      </div>

      {/* ── Liste des streamers ── */}
      {loading ? (
        <div style={{ textAlign:'center', color:'#94a3b8', padding:32 }}>Chargement…</div>
      ) : members.length === 0 ? (
        <div style={{ textAlign:'center', color:'#94a3b8', padding:32, fontSize:13 }}>Aucun membre pour cette période</div>
      ) : isMobile ? (
        /* Mobile : cartes horizontales */
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {members.map(m => {
            const isLive = !!m.tiktok_live;
            const tiktokUrl = isLive ? `https://www.tiktok.com/@${m.username}/live` : `https://www.tiktok.com/@${m.username}`;
            const metrics = [
              { v: m.live_days ?? 0,                                t: objectives.days },
              { v: Math.floor((m.live_duration_minutes ?? 0) / 60), t: objectives.hours },
              { v: m.diamonds ?? 0,                                 t: objectives.diamonds },
            ];
            return (
              <div key={m.id} style={{ background:'#fff', borderRadius:12, border:`1px solid ${isLive?'#fecaca':'#e2e8f0'}`, padding:'12px 14px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                  <LiveAvatar url={m.avatar_url} initials={m.display_name.slice(0,2).toUpperCase()} size={38} isLive={isLive} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.display_name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>@{m.username}</div>
                  </div>
                  <a href={tiktokUrl} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, background: isLive?'#dc2626':'#f1f5f9', textDecoration:'none', flexShrink:0 }}
                    onClick={e => e.stopPropagation()}>
                    <TikTokIcon color={isLive ? '#fff' : '#374151'} size={14} />
                  </a>
                </div>
                {/* Stats */}
                <div style={{ display:'flex', gap:10, fontSize:11, color:'#64748b', marginBottom:8, flexWrap:'wrap' }}>
                  <span>{fmtN(m.diamonds)} 💎</span>
                  <span>·</span><span>{fmtDur(m.live_duration_minutes)}</span>
                  {(m.team_niveau||0) > 0 && <><span>·</span><span style={{ color:'#6366f1', fontWeight:600 }}>📈 {fmtR(m.team_niveau)}</span></>}
                  {(m.team_activite||0) > 0 && <><span>·</span><span style={{ color:'#f59e0b', fontWeight:600 }}>⚡ {fmtR(m.team_activite)}</span></>}
                </div>
                {/* Jauge objectifs */}
                <div style={{ display:'flex', gap:2 }}>
                  {metrics.map((mt, i) => {
                    const pct = Math.min(1, mt.t > 0 ? mt.v / mt.t : 0);
                    const radius = i === 0 ? '4px 0 0 4px' : i === 2 ? '0 4px 4px 0' : '0';
                    return <div key={i} style={{ flex:1, height:5, background:objColor(pct), borderRadius:radius }} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop : tableau */
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', overflow:'hidden' }}>
          {/* En-tête colonnes */}
          <div style={{ display:'grid', gridTemplateColumns:'8px 1fr 130px 120px 90px 90px 90px 52px', gap:8, padding:'9px 16px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
            <span /><span style={sdTh}>Streamer</span><span style={sdTh}>@TikTok</span>
            <span style={sdTh}>💎 Diamants</span><span style={sdTh}>Durée</span>
            <span style={{ ...sdTh, textAlign:'center' }}>Montée niv.</span>
            <span style={{ ...sdTh, textAlign:'center' }}>Activité</span>
            <span />
          </div>
          {members.map((m, idx) => {
            const isLive = !!m.tiktok_live;
            const tiktokUrl = isLive ? `https://www.tiktok.com/@${m.username}/live` : `https://www.tiktok.com/@${m.username}`;
            const metrics = [
              { v: m.live_days ?? 0,                                t: objectives.days },
              { v: Math.floor((m.live_duration_minutes ?? 0) / 60), t: objectives.hours },
              { v: m.diamonds ?? 0,                                 t: objectives.diamonds },
            ];
            const cellH = Math.floor((34 - 4) / 3);
            return (
              <div key={m.id} style={{ display:'grid', gridTemplateColumns:'8px 1fr 130px 120px 90px 90px 90px 52px', gap:8, padding:'10px 16px', borderBottom: idx < members.length-1 ? '1px solid #f1f5f9' : 'none', alignItems:'center' }}>
                {/* Jauge verticale */}
                <div style={{ display:'flex', flexDirection:'column', gap:2, width:8, height:34, flexShrink:0 }}>
                  {[...metrics].reverse().map((mt, i) => {
                    const pct = Math.min(1, mt.t > 0 ? mt.v / mt.t : 0);
                    const r = i===0?'4px 4px 0 0':i===2?'0 0 4px 4px':'0';
                    return <div key={i} style={{ height:cellH, background:objColor(pct), borderRadius:r }} />;
                  })}
                </div>
                {/* Avatar + nom */}
                <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                  <LiveAvatar url={m.avatar_url} initials={m.display_name.slice(0,2).toUpperCase()} size={34} isLive={isLive} />
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.display_name}</div>
                  </div>
                </div>
                {/* @TikTok */}
                <div style={{ color:'#64748b', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>@{m.username}</div>
                {/* Diamants */}
                <div style={{ color:'#0f172a', fontSize:13, fontWeight:600 }}>{fmtN(m.diamonds)}</div>
                {/* Durée */}
                <div style={{ color:'#64748b', fontSize:13 }}>{fmtDur(m.live_duration_minutes)}</div>
                {/* Montée niv. */}
                <div style={{ color:(m.team_niveau||0)>0?'#6366f1':'#94a3b8', fontSize:12, fontWeight:600, textAlign:'center' }}>
                  {(m.team_niveau||0)>0 ? fmtR(m.team_niveau) : '—'}
                </div>
                {/* Activité */}
                <div style={{ color:(m.team_activite||0)>0?'#f59e0b':'#94a3b8', fontSize:12, fontWeight:600, textAlign:'center' }}>
                  {(m.team_activite||0)>0 ? fmtR(m.team_activite) : '—'}
                </div>
                {/* Action TikTok */}
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <a href={tiktokUrl} target="_blank" rel="noreferrer"
                    title={isLive ? 'Rejoindre le live' : 'Voir le profil TikTok'}
                    style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, background: isLive?'#dc2626':'#f1f5f9', textDecoration:'none', flexShrink:0 }}>
                    <TikTokIcon color={isLive ? '#fff' : '#374151'} size={14} />
                  </a>
                </div>
              </div>
            );
          })}
          {/* Ligne total */}
          <div style={{ display:'grid', gridTemplateColumns:'8px 1fr 130px 120px 90px 90px 90px 52px', gap:8, padding:'9px 16px', background:'#f8fafc', borderTop:'2px solid #e2e8f0', alignItems:'center' }}>
            <span /><div style={{ fontSize:12, fontWeight:700, color:'#374151' }}>TOTAL</div>
            <span /><div style={{ color:'#0f172a', fontSize:13, fontWeight:700 }}>{fmtN(totalDiamonds)}</div>
            <span />
            <div style={{ color:'#6366f1', fontSize:12, fontWeight:800, textAlign:'center' }}>{(remu?.total_team_niveau||0)>0?fmtR(remu?.total_team_niveau):'—'}</div>
            <div style={{ color:'#f59e0b', fontSize:12, fontWeight:800, textAlign:'center' }}>{(remu?.total_team_activite||0)>0?fmtR(remu?.total_team_activite):'—'}</div>
            <span />
          </div>
        </div>
      )}
    </div>
  );
}

const sdTh: React.CSSProperties = { color:'#94a3b8', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:0.7 };

const SD_MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
function fmtPeriodLabel(p: string): string {
  const mYM = p.match(/^(\d{4})-(\d{2})$/);
  if (mYM) return `${SD_MONTHS_FR[parseInt(mYM[2]) - 1]} ${mYM[1]}`;
  const mRange = p.match(/^(\d{4})-(\d{2})-\d{2}\s*~\s*\d{4}-\d{2}-\d{2}$/);
  if (mRange) return `${SD_MONTHS_FR[parseInt(mRange[2]) - 1]} ${mRange[1]}`;
  return p;
}

function TikTokIcon({ color = '#000', size = 16 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z"/>
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function LightField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', background: 'none', border: 'none',
      borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`,
      color: active ? '#2563eb' : '#64748b',
      fontSize: 13, fontWeight: active ? 600 : 400,
      cursor: 'pointer', display: 'flex', alignItems: 'center',
      transition: 'all 0.15s', marginBottom: -1,
    }}>
      {children}
    </button>
  );
}

const inputLight: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff',
  color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  background: '#2563eb', color: '#fff', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0',
  background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer',
};
